import { toPng } from 'html-to-image'

// Same filter as downloadChart: skip absolutely-positioned overlays and
// anything explicitly marked to be omitted from downloads (buttons, controls).
const filter = (node: HTMLElement | SVGElement) => {
  let classNameStr = ''

  if ('className' in node) {
    if (typeof node.className === 'string') {
      classNameStr = node.className
    } else if (node.className && 'baseVal' in node.className) {
      classNameStr = node.className.baseVal
    }
  }

  return !classNameStr.includes('absolute') && !classNameStr.includes('omit-from-download')
}

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = dataUrl
  })

// Uniform vertical distance (mm) between every block and the page margin, so
// spacing looks the same throughout the document.
const SPACING = 10

/**
 * Renders each given node (a title block or one dashboard widget) to an image
 * and lays them out in a single A4 PDF, one node per block, with a uniform
 * vertical gap. A node that would overflow the current page starts a new one,
 * so a node is never split across pages.
 */
export const downloadDashboardPdf = async (nodes: HTMLElement[], fileName: string) => {
  try {
    if (!nodes.length) return

    const { default: jsPDF } = await import('jspdf')

    const pdf = new jsPDF('p', 'mm', 'a4')
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const contentWidth = pageWidth - SPACING * 2

    let cursorY = SPACING

    for (const node of nodes) {
      const dataUrl = await toPng(node, {
        backgroundColor: '#FFFFFF',
        pixelRatio: 2,
        quality: 1.0,
        includeQueryParams: true,
        filter
      })

      const img = await loadImage(dataUrl)
      const imgHeight = (img.naturalHeight * contentWidth) / img.naturalWidth

      // Start a new page if this node would overflow the current one
      // (unless we're already at the top of a page).
      if (cursorY + imgHeight > pageHeight - SPACING && cursorY > SPACING) {
        pdf.addPage()
        cursorY = SPACING
      }

      pdf.addImage(dataUrl, 'PNG', SPACING, cursorY, contentWidth, imgHeight)
      cursorY += imgHeight + SPACING
    }

    pdf.save(fileName)
  } catch (error) {
    console.error('Error downloading dashboard PDF:', error)
  }
}
